import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { useToast } from "@/hooks/use-toast";
import { useSendContactForm } from "@/hooks/api/mutate/useSendContactForm";
import type { SendContactFormRequest } from "@/api/types/contactForm";
import { SharedFooter } from "@/components/common/SharedFooter";
import { useAuth } from "@/contexts/AuthContext";

const CONTACT_TAB_KEYS = ["general", "gdpr"] as const;
type ContactTab = (typeof CONTACT_TAB_KEYS)[number];

const TAB_LABELS: Record<ContactTab, string> = {
  general: "Contact General",
  gdpr: "Drepturi GDPR",
};

const SUBJECT_OPTIONS: Record<ContactTab, string[]> = {
  general: [
    "Informatii generale",
    "Solicitare comerciala",
    "Suport tehnic",
    "Parteneriat",
    "Altele",
  ],
  gdpr: [
    "Drept de acces la date",
    "Drept la rectificare",
    "Drept la stergere",
    "Drept la portabilitate",
    "Drept la opozitie",
    "Altele",
  ],
};

const contactSchema = z.object({
  name: z.string().min(2, "Numele trebuie sa aiba cel putin 2 caractere"),
  email: z.string().email("Adresa de email nu este valida"),
  company: z.string().optional(),
  phone: z.string().optional(),
  reason: z.string().min(1, "Selectati un subiect"),
  message: z.string().min(10, "Mesajul trebuie sa aiba cel putin 10 caractere"),
  privacyAccepted: z.literal(true, {
    errorMap: () => ({
      message: "Trebuie sa acceptati Politica de Confidentialitate",
    }),
  }),
});

type ContactFormValues = z.infer<typeof contactSchema>;

export default function Contact() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isLoggedIn = !!user;
  const { toast } = useToast();
  const { mutate: sendForm, isPending } = useSendContactForm();
  const [activeTab, setActiveTab] = useState<ContactTab>("general");

  const form = useForm<ContactFormValues>({
    resolver: zodResolver(contactSchema),
    defaultValues: {
      name: "",
      email: "",
      company: "",
      phone: "",
      reason: "",
      message: "",
      privacyAccepted: undefined,
    },
  });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    document.title = "Contact — NIS2PASS";
    const meta = document.querySelector('meta[name="description"]');
    if (meta) {
      meta.setAttribute(
        "content",
        "Contacteaza echipa NIS2PASS pentru intrebari, suport tehnic sau exercitarea drepturilor GDPR."
      );
    }
  }, []);

  useEffect(() => {
    form.setValue("reason", "");
  }, [activeTab, form]);

  const onSubmit = (values: ContactFormValues) => {
    const request: SendContactFormRequest = {
      name: values.name,
      email: values.email,
      reason: values.reason,
      message: values.message,
    };

    sendForm(request, {
      onSuccess: () => {
        toast({
          title: "Mesaj trimis",
          description:
            "Va multumim! Vom reveni cu un raspuns in cel mai scurt timp.",
        });
        form.reset();
      },
      onError: () => {
        toast({
          title: "Eroare",
          description:
            "Nu am putut trimite mesajul. Va rugam incercati din nou.",
          variant: "destructive",
        });
      },
    });
  };

  const fieldErrors = form.formState.errors;

  return (
    <div className="cp-page">
      <style>{`
        :root {
          --c-navy: #0F172A;
          --c-navy-dark: #1E293B;
          --c-navy-darkest: #0B1220;
          --c-blue: #2563EB;
          --c-blue-dark: #1D4ED8;
          --c-blue-light: #60A5FA;
          --c-orange: #FF8400;
          --c-orange-hover: #FF9520;
          --c-bg-light: #F8FAFC;
          --c-bg-page: #F7FAFF;
          --c-border-light: #E2E8F0;
          --c-border-mid: #CBD5E1;
          --c-text-body: #475569;
          --c-text-muted: #64748B;
          --c-text-dim: #94A3B8;
          --c-text-faint: #CBD5E1;
          --c-blue-light: #60A5FA;
          --c-white: #FFF;
          --c-black: #111;
        }

        .cp-page { background: var(--c-white); color: var(--c-navy); font-family: 'Sora', system-ui, sans-serif; min-height: 100vh; display: flex; flex-direction: column; }
        .cp-page * { box-sizing: border-box; margin: 0; padding: 0; }
        .cp-page a { text-decoration: none; color: inherit; }

        /* NAV */
        .cp-nav { width: 100%; background: var(--c-navy); padding: 28px 0; display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
        .cp-nav-inner { max-width: 1200px; width: 100%; padding: 0 24px; margin: 0 auto; display: flex; align-items: center; justify-content: space-between; }
        .cp-nav-logo { display: flex; align-items: center; gap: 8px; cursor: pointer; text-decoration: none; }
        .cp-nav-logo-mark { width: 14px; height: 14px; background: #22C55E; border-radius: 2px; flex-shrink: 0; }
        .cp-nav-brand { font-family: 'Sora', sans-serif; font-size: 18px; font-weight: 700; color: var(--c-white); }
        .cp-nav-links { display: flex; align-items: center; gap: 24px; }
        .cp-nav-link { font-family: 'Roboto Mono', monospace; font-size: 12px; color: var(--c-text-faint); cursor: pointer; transition: color 150ms; background: none; border: none; padding: 0; }
        .cp-nav-link:hover { color: var(--c-white); }
        .cp-nav-link.active { color: var(--c-blue-light); }
        .cp-nav-btns { display: flex; align-items: center; gap: 12px; }
        .cp-nav-ghost { font-family: 'Roboto Mono', monospace; font-size: 12px; color: var(--c-text-faint); background: transparent; border: 1px solid #334155; padding: 10px 18px; border-radius: 999px; cursor: pointer; transition: all 200ms; }
        .cp-nav-ghost:hover { border-color: var(--c-blue-light); color: var(--c-white); background: rgba(96,165,250,0.08); }
        .cp-nav-cta { font-family: 'Roboto Mono', monospace; font-size: 12px; color: var(--c-black); background: var(--c-orange); border: none; padding: 10px 18px; border-radius: 999px; cursor: pointer; font-weight: 500; transition: all 200ms; }
        .cp-nav-cta:hover { background: var(--c-orange-hover); transform: translateY(-1px); box-shadow: 0 4px 12px rgba(255,132,0,0.3); }

        /* MAIN CONTENT */
        .cp-main { flex: 1; max-width: 1120px; width: 100%; margin: 0 auto; padding: 80px 24px; }

        /* HEADER */
        .cp-label { font-family: 'Roboto Mono', monospace; font-size: 12px; color: var(--c-blue); letter-spacing: 1.4px; text-transform: uppercase; margin-bottom: 12px; }
        .cp-title { font-family: 'Sora', sans-serif; font-size: 38px; font-weight: 700; color: var(--c-navy); letter-spacing: -1.2px; line-height: 1.08; margin-bottom: 12px; }
        .cp-subtitle { font-family: 'Roboto Mono', monospace; font-size: 14px; color: var(--c-text-body); margin-bottom: 32px; max-width: 720px; line-height: 1.45; }

        /* TABS */
        .cp-tabs { display: flex; gap: 8px; margin-bottom: 32px; overflow-x: auto; -webkit-overflow-scrolling: touch; }
        .cp-tabs::-webkit-scrollbar { display: none; }
        .cp-tab { font-family: 'Roboto Mono', monospace; font-size: 11px; letter-spacing: 1px; text-transform: uppercase; padding: 8px 14px; cursor: pointer; border: 1px solid var(--c-border-mid); background: var(--c-white); color: #334155; transition: all 150ms; white-space: nowrap; flex-shrink: 0; }
        .cp-tab:hover { border-color: var(--c-navy); }
        .cp-tab.active { background: #111827; border-color: #111827; color: var(--c-white); }

        /* TWO-COLUMN LAYOUT */
        .cp-body { display: flex; gap: 32px; align-items: flex-start; }
        .cp-form-col { flex: 1; min-width: 0; }
        .cp-info-col { width: 380px; flex-shrink: 0; }

        /* GDPR NOTICE */
        .cp-gdpr-notice { font-family: 'Roboto Mono', monospace; font-size: 12px; color: var(--c-text-body); line-height: 1.45; margin-bottom: 24px; padding: 16px; background: var(--c-bg-light); border: 1px solid var(--c-border-light); border-radius: 6px; }

        /* FORM */
        .cp-form-title { font-family: 'Sora', sans-serif; font-size: 22px; font-weight: 700; color: var(--c-navy); margin-bottom: 4px; }
        .cp-form-subtitle { font-family: 'Roboto Mono', monospace; font-size: 12px; color: var(--c-text-muted); margin-bottom: 24px; }

        .cp-form-row { display: flex; gap: 16px; margin-bottom: 16px; }
        .cp-form-row > .cp-field { flex: 1; }
        .cp-form-row-full { margin-bottom: 16px; }

        .cp-field {}
        .cp-field-label { font-size: 12px; font-weight: 700; color: var(--c-navy); display: block; margin-bottom: 6px; }
        .cp-field-label .req { color: #EF4444; }
        .cp-field-input { width: 100%; font-family: 'Roboto Mono', monospace; font-size: 13px; color: var(--c-navy); background: var(--c-bg-light); border: 1px solid var(--c-border-light); border-radius: 6px; padding: 10px 14px; outline: none; transition: border-color 150ms; }
        .cp-field-input::placeholder { color: var(--c-text-dim); }
        .cp-field-input:focus { border-color: var(--c-blue); }
        .cp-field-input.error { border-color: #EF4444; }
        .cp-field-select { width: 100%; font-family: 'Roboto Mono', monospace; font-size: 13px; color: var(--c-navy); background: var(--c-bg-light); border: 1px solid var(--c-border-light); border-radius: 6px; padding: 10px 14px; outline: none; transition: border-color 150ms; appearance: none; background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e"); background-position: right 10px center; background-repeat: no-repeat; background-size: 20px; cursor: pointer; }
        .cp-field-select:focus { border-color: var(--c-blue); }
        .cp-field-select.error { border-color: #EF4444; }
        .cp-field-textarea { width: 100%; font-family: 'Roboto Mono', monospace; font-size: 13px; color: var(--c-navy); background: var(--c-bg-light); border: 1px solid var(--c-border-light); border-radius: 6px; padding: 10px 14px; outline: none; transition: border-color 150ms; resize: vertical; height: 120px; }
        .cp-field-textarea::placeholder { color: var(--c-text-dim); }
        .cp-field-textarea:focus { border-color: var(--c-blue); }
        .cp-field-textarea.error { border-color: #EF4444; }
        .cp-field-error { font-family: 'Roboto Mono', monospace; font-size: 11px; color: #EF4444; margin-top: 4px; }

        /* CHECKBOX */
        .cp-checkbox-row { display: flex; align-items: flex-start; gap: 10px; margin: 20px 0 24px; }
        .cp-checkbox { width: 16px; height: 16px; flex-shrink: 0; margin-top: 2px; accent-color: var(--c-blue); cursor: pointer; }
        .cp-checkbox-label { font-size: 12px; color: var(--c-text-body); line-height: 1.4; }
        .cp-checkbox-label a { color: var(--c-blue); text-decoration: underline; }
        .cp-checkbox-label a:hover { color: var(--c-blue-dark); }

        /* SUBMIT */
        .cp-submit { width: 100%; height: 48px; background: var(--c-orange); color: var(--c-black); border: none; border-radius: 999px; font-family: 'Roboto Mono', monospace; font-size: 14px; font-weight: 500; cursor: pointer; transition: all 150ms; }
        .cp-submit:hover { background: var(--c-orange-hover); }
        .cp-submit:disabled { opacity: 0.6; cursor: not-allowed; }

        /* INFO COLUMN */
        .cp-info-title { font-family: 'Sora', sans-serif; font-size: 18px; font-weight: 700; color: var(--c-navy); margin-bottom: 16px; }
        .cp-info-item { font-family: 'Roboto Mono', monospace; font-size: 12px; color: var(--c-text-body); margin-bottom: 8px; line-height: 1.4; }

        .cp-info-card { background: var(--c-bg-light); border: 1px solid var(--c-border-light); border-radius: 6px; padding: 16px 18px; margin: 20px 0; }
        .cp-info-card-name { font-family: 'Sora', sans-serif; font-size: 14px; font-weight: 700; color: var(--c-navy); margin-bottom: 4px; }
        .cp-info-card-detail { font-family: 'Roboto Mono', monospace; font-size: 11px; color: var(--c-text-muted); margin-bottom: 2px; }

        .cp-info-section-title { font-family: 'Sora', sans-serif; font-size: 14px; font-weight: 700; color: var(--c-navy); margin: 20px 0 10px; }
        .cp-info-response { font-family: 'Roboto Mono', monospace; font-size: 11px; color: var(--c-text-body); margin-bottom: 6px; }

        /* RESPONSIVE */
        @media (max-width: 900px) {
          .cp-body { flex-direction: column; }
          .cp-info-col { width: 100%; }
        }
        @media (max-width: 768px) {
          .cp-main { padding: 48px 24px; }
          .cp-title { font-size: 28px; letter-spacing: -0.8px; }
          .cp-form-row { flex-direction: column; gap: 16px; }
          .cp-nav-links, .cp-nav-btns { display: none; }
        }
      `}</style>

      {/* NAV */}
      <nav className="cp-nav">
        <div className="cp-nav-inner">
          <a href="/" className="cp-nav-logo">
            <span className="cp-nav-logo-mark" />
            <span className="cp-nav-brand">NIS2PASS</span>
          </a>
          <div className="cp-nav-links">
            <button
              type="button"
              className="cp-nav-link"
              onClick={() => navigate("/")}
            >
              Platforma
            </button>
            <button
              type="button"
              className="cp-nav-link"
              onClick={() => navigate("/termeni-si-conditii")}
            >
              Legal
            </button>
            <button
              type="button"
              className="cp-nav-link active"
              onClick={() => navigate("/contact")}
            >
              Contact
            </button>
          </div>
          <div className="cp-nav-btns">
            {isLoggedIn ? (
              <>
                <button
                  type="button"
                  className="cp-nav-cta"
                  onClick={() => navigate("/dashboard")}
                >
                  Dashboard
                </button>
                <button
                  type="button"
                  className="cp-nav-ghost"
                  onClick={() => navigate("/logout")}
                >
                  Logout
                </button>
              </>
            ) : (
              <>
                <button
                  type="button"
                  className="cp-nav-ghost"
                  onClick={() => navigate("/auth?mode=signin")}
                >
                  Intra in cont
                </button>
                <button
                  type="button"
                  className="cp-nav-cta"
                  onClick={() => navigate("/auth")}
                >
                  Creeaza cont gratuit
                </button>
              </>
            )}
          </div>
        </div>
      </nav>

      {/* MAIN */}
      <main className="cp-main">
        <p className="cp-label">CONTACT NIS2PASS</p>
        <h1 className="cp-title">Contacteaza echipa NIS2PASS</h1>
        <p className="cp-subtitle">
          Intrebari generale, solicitari comerciale, suport tehnic sau
          exercitarea drepturilor GDPR — contacteaza-ne prin formularul de mai
          jos.
        </p>

        {/* TABS */}
        <div className="cp-tabs">
          {CONTACT_TAB_KEYS.map((key) => (
            <button
              key={key}
              type="button"
              className={`cp-tab${activeTab === key ? " active" : ""}`}
              onClick={() => setActiveTab(key)}
            >
              {TAB_LABELS[key]}
            </button>
          ))}
        </div>

        {/* BODY */}
        <div className="cp-body">
          {/* LEFT — FORM */}
          <div className="cp-form-col">
            {activeTab === "gdpr" && (
              <div className="cp-gdpr-notice">
                In conformitate cu Regulamentul (UE) 2016/679 (GDPR), aveti
                dreptul de a solicita accesul, rectificarea, stergerea sau
                portabilitatea datelor dumneavoastra personale.
              </div>
            )}

            <h2 className="cp-form-title">Trimite un mesaj</h2>
            <p className="cp-form-subtitle">
              Completeaza formularul si te contactam in maximum 24 de ore
              lucratoare.
            </p>

            <form onSubmit={form.handleSubmit(onSubmit)}>
              {/* Row 1: Name + Email */}
              <div className="cp-form-row">
                <div className="cp-field">
                  <label className="cp-field-label">
                    Nume si prenume <span className="req">*</span>
                  </label>
                  <input
                    type="text"
                    className={`cp-field-input${fieldErrors.name ? " error" : ""}`}
                    placeholder="Nume complet"
                    {...form.register("name")}
                  />
                  {fieldErrors.name && (
                    <p className="cp-field-error">{fieldErrors.name.message}</p>
                  )}
                </div>
                <div className="cp-field">
                  <label className="cp-field-label">
                    Email <span className="req">*</span>
                  </label>
                  <input
                    type="email"
                    className={`cp-field-input${fieldErrors.email ? " error" : ""}`}
                    placeholder="email@companie.ro"
                    {...form.register("email")}
                  />
                  {fieldErrors.email && (
                    <p className="cp-field-error">
                      {fieldErrors.email.message}
                    </p>
                  )}
                </div>
              </div>

              {/* Row 2: Company + Phone */}
              <div className="cp-form-row">
                <div className="cp-field">
                  <label className="cp-field-label">Companie</label>
                  <input
                    type="text"
                    className="cp-field-input"
                    placeholder="Numele companiei"
                    {...form.register("company")}
                  />
                </div>
                <div className="cp-field">
                  <label className="cp-field-label">Telefon</label>
                  <input
                    type="tel"
                    className="cp-field-input"
                    placeholder="+40 7XX XXX XXX"
                    {...form.register("phone")}
                  />
                </div>
              </div>

              {/* Row 3: Subject */}
              <div className="cp-form-row-full">
                <div className="cp-field">
                  <label className="cp-field-label">
                    Subiect <span className="req">*</span>
                  </label>
                  <select
                    className={`cp-field-select${fieldErrors.reason ? " error" : ""}`}
                    {...form.register("reason")}
                    defaultValue=""
                  >
                    <option value="" disabled>
                      Selecteaza subiectul
                    </option>
                    {SUBJECT_OPTIONS[activeTab].map((opt) => (
                      <option key={opt} value={opt}>
                        {opt}
                      </option>
                    ))}
                  </select>
                  {fieldErrors.reason && (
                    <p className="cp-field-error">
                      {fieldErrors.reason.message}
                    </p>
                  )}
                </div>
              </div>

              {/* Row 4: Message */}
              <div className="cp-form-row-full">
                <div className="cp-field">
                  <label className="cp-field-label">
                    Mesaj <span className="req">*</span>
                  </label>
                  <textarea
                    className={`cp-field-textarea${fieldErrors.message ? " error" : ""}`}
                    placeholder="Scrie mesajul tau aici..."
                    {...form.register("message")}
                  />
                  {fieldErrors.message && (
                    <p className="cp-field-error">
                      {fieldErrors.message.message}
                    </p>
                  )}
                </div>
              </div>

              {/* Checkbox */}
              <div className="cp-checkbox-row">
                <input
                  type="checkbox"
                  className="cp-checkbox"
                  id="privacy-accept"
                  {...form.register("privacyAccepted")}
                />
                <label className="cp-checkbox-label" htmlFor="privacy-accept">
                  Am citit si sunt de acord cu{" "}
                  <a
                    href="/politica-de-confidentialitate"
                    target="_blank"
                    rel="noopener"
                  >
                    Politica de Confidentialitate
                  </a>
                </label>
              </div>
              {fieldErrors.privacyAccepted && (
                <p className="cp-field-error" style={{ marginBottom: 16 }}>
                  {fieldErrors.privacyAccepted.message}
                </p>
              )}

              {/* Submit */}
              <button type="submit" className="cp-submit" disabled={isPending}>
                {isPending ? "Se trimite..." : "Trimite mesajul"}
              </button>
            </form>
          </div>

          {/* RIGHT — INFO */}
          <div className="cp-info-col">
            <h3 className="cp-info-title">Informatii de contact</h3>
            <p className="cp-info-item">Email general: contact@nis2pass.ro</p>
            <p className="cp-info-item">
              Protectia datelor: dataprotection@nis2pass.ro
            </p>
            <p className="cp-info-item">Facturare: contact@nis2pass.ro</p>

            <div className="cp-info-card">
              <p className="cp-info-card-name">Compliance Made Easy SRL</p>
              <p className="cp-info-card-detail">
                CUI: 48993456 · Nr.Reg.Com.: J2023019988400
              </p>
              <p className="cp-info-card-detail">
                Str. Baba Novac 19A, Bucuresti, Sectorul 3
              </p>
            </div>

            <h4 className="cp-info-section-title">Timpuri de raspuns</h4>
            <p className="cp-info-response">
              Intrebari generale: 24h lucratoare
            </p>
            <p className="cp-info-response">Suport tehnic: 12h lucratoare</p>
            <p className="cp-info-response">
              Solicitari GDPR: 30 zile calendaristice
            </p>
            <p className="cp-info-response">Dispute plati: 48h lucratoare</p>
          </div>
        </div>
      </main>

      {/* FOOTER */}
      <SharedFooter />
    </div>
  );
}
